import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import GoogleMapReact from 'google-map-react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { TravelTypes, googleApiKey } from '../../constants';
import Marker from './Marker';
import StaticMapPreview from './StaticMapPreview';
import getHotelRateToDisplay from '../../helpers/getHotelRateToDisplay';
import getHotelPriceDisplayPreference from '../../helpers/selectors/getHotelPriceDisplayPreference';
import currencySymbol from '../../helpers/currencySymbol';
import getHotelRate from '../../helpers/selectors/getHotelRate';
import checkDoShowMarkupRates from '../../helpers/selectors/checkDoShowMarkupRates';
import getMapViewport from '../../helpers/getMapViewport';
import useElementSize from '../../hooks/useElementSize';
import HotelMapTooltip from './HotelMapTooltip';
import TourMapTooltip from './TourMapTooltip';
import getTourRateToDisplay from '../../helpers/getTourRateToDisplay';
import MapMarkerLegend from './MapMarkerLegend';
import { getTravelAgency } from '../../store/slices/user';
import { activateMap } from '../../store/slices/tripSearch';

// Coarse enough that dragging a window edge does not re-render every marker per pixel.
const CONTAINER_MEASURE_STEP = 20;

const MOBILE_BREAKPOINT = 640;

interface DisplayMapProps {
  onMarkerClick: (markerKey: string, pageNumber: number) => void;
  hoveredMarkerKey?: string;
  selectedMarkerKey?: string;
  markers: Array<any>; // Replace with the actual type of your markers
  tripId?: string;
  // The map itself — bounds, pins, hover and selection — is the same for every travel type. What a pin
  // says and what its tooltip shows is not: a hotel has a nightly rate, a tour a single price.
  mode?: number;
}

const DisplayMap: React.FC<DisplayMapProps> = ({
  onMarkerClick,
  hoveredMarkerKey,
  markers,
  selectedMarkerKey,
  mode,
  tripId,
}) => {
  const isTripSearch = !tripId;
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isTourMap = mode === TravelTypes.tour;
  const doShowMarkupRate = useSelector(checkDoShowMarkupRates({ mode: TravelTypes.hotel, tripId }));
  const travelAgency = useSelector(getTravelAgency);
  const mapRef = useRef<any>(null);
  const pricePreference = useSelector(getHotelPriceDisplayPreference());
  let keyPropertyName = 'hotelKey';
  if (markers && markers.length && !markers[0][keyPropertyName] && markers[0].key) {
    keyPropertyName = 'key';
  }

  const doShowMapHighlighting = travelAgency?.settings?.doShowMapHighlighting;
  const doHideAgentPreferedHotelLabel = travelAgency?.settings?.hideAgentPreferedHotelLabel;
  const isAnyRateCodeSelected = useSelector(
    (state: any) => state?.tripSearch?.searchObj?.hotel?.rateCodes?.length > 0,
  );
  const selectedSearchMode = useSelector((state: any) => state?.tripSearch?.selectedSearchMode);
  const isMapActivated = useSelector((state: any) => state?.tripSearch?.isMapActivated);

  const [activeMarker, setActiveMarker] = useState<any>(null);
  const mapMarkerLegendRef = useRef<HTMLDivElement | null>(null);

  const { ref: containerRef, width, height } = useElementSize(CONTAINER_MEASURE_STEP);

  const handleMarkerMouseEnter = (marker: any) => {
    setActiveMarker(marker);
  };

  const handleMarkerMouseLeave = () => {
    setActiveMarker(null);
  };

  // The static preview and the dynamic map read their framing from here, which is what
  // lets one replace the other without the view jumping.
  const viewport = useMemo(
    () => getMapViewport({ markers, width, height }),
    [markers, width, height],
  );
  const viewportKey = viewport
    ? `${viewport.center.lat},${viewport.center.lng},${viewport.zoom}`
    : '';

  const markerCount = markers ? markers.length : 0;
  const mapAnalyticsProperties = useMemo(
    () => ({
      view: isTripSearch ? 'trip_search' : 'trip',
      searchMode: selectedSearchMode,
      device:
        typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
          ? 'mobile'
          : 'desktop',
      markerCount,
      isAnyRateCodeSelected,
      travelAgencyId: travelAgency && travelAgency.id,
    }),
    [isTripSearch, selectedSearchMode, markerCount, isAnyRateCodeSelected, travelAgency],
  );

  const handleActivate = useCallback(() => {
    track('map_activated', mapAnalyticsProperties);

    // activateMap carries no payload, but this slice is large enough that Redux Toolkit
    // 1.4 widens every one of its action creators to ActionCreatorWithPayload<any>, which
    // type-checks as requiring one argument. Hence the explicit undefined.
    dispatch(activateMap(undefined));
  }, [dispatch, track, mapAnalyticsProperties]);

  // Re-frame an already open map when a new search moves the markers. The key rather
  // than the object keeps a user's own panning from being undone on every re-render.
  useEffect(() => {
    if (!mapRef.current || !viewport) {
      return;
    }

    mapRef.current.setCenter(viewport.center);
    mapRef.current.setZoom(viewport.zoom);
    // oxlint-disable-next-line react/exhaustive-deps
  }, [viewportKey]);

  const handleLoaded = ({ map }: any) => {
    mapRef.current = map;

    if (mapMarkerLegendRef && doShowMapHighlighting && !isTourMap) {
      map.controls[(window as any).google.maps.ControlPosition.LEFT_BOTTOM].push(
        mapMarkerLegendRef.current,
      );
    }
  };

  const renderMarker = (marker: any) => (
    <Marker
      selected={selectedMarkerKey === marker[keyPropertyName]}
      hovered={!!hoveredMarkerKey && hoveredMarkerKey === marker[keyPropertyName]}
      key={marker[keyPropertyName]}
      lat={marker.latitude}
      lng={marker.longitude}
      text={
        isTourMap
          ? getTourRateToDisplay(marker)
          : getHotelRateToDisplay(
              pricePreference,
              getHotelRate({
                doShowMarkupRate,
                rate: {
                  averageNightlyRate: marker.avgNightlyRate,
                  averageNightlyRateBeforeTax: marker.avgNightlyRateBeforeTax,
                  currencyCode: marker.currencyCode,
                  markupAverageNightlyRate: marker.markupAvgNightlyRate,
                  markupAverageNightlyRateBeforeTax: marker.markupAvgNightlyRateBeforeTax,
                  markupPrice: marker.markupCost,
                  originalPrice: marker.cost,
                },
              }),
            )
      }
      markerKey={marker[keyPropertyName]}
      pageNumber={marker.pageNumber}
      onClick={onMarkerClick}
      currency={currencySymbol(marker.currencyCode)}
      onMouseEnter={() => handleMarkerMouseEnter(marker)}
      onMouseLeave={handleMarkerMouseLeave}
      isAgentPreferredHotel={marker.isAgentPreferredHotel && !doHideAgentPreferedHotelLabel}
      hasRateCodeMatch={marker.hasRateCodeMatch}
      isAnyRateCodeSelected={isAnyRateCodeSelected}
      doShowMapHighlighting={doShowMapHighlighting}
    />
  );

  // Until the container has been measured there is no viewport to frame either map with.
  // Falling through to the dynamic map here would download the Maps JavaScript SDK on
  // every results render, which is the cost this whole component exists to avoid.
  const renderMapSurface = () => {
    if (!viewport) {
      return null;
    }

    if (!isMapActivated) {
      return (
        <StaticMapPreview
          initialViewport={viewport}
          initialWidth={width}
          initialHeight={height}
          onActivate={handleActivate}
        />
      );
    }

    return (
      <GoogleMapReact
        yesIWantToUseGoogleMapApiInternals
        bootstrapURLKeys={{ key: googleApiKey }}
        defaultCenter={viewport.center}
        defaultZoom={viewport.zoom}
        onGoogleApiLoaded={handleLoaded}
      >
        {markers.map(renderMarker)}
        {activeMarker &&
          (isTourMap ? (
            <TourMapTooltip
              marker={activeMarker}
              lat={activeMarker.latitude}
              lng={activeMarker.longitude}
            />
          ) : (
            <HotelMapTooltip
              marker={activeMarker}
              lat={activeMarker.latitude}
              lng={activeMarker.longitude}
            />
          ))}
      </GoogleMapReact>
    );
  };

  if (!markers || !markers.length) {
    return <></>;
  }

  return (
    <div
      className={clsx('sticky top-0 h-screen flex-shrink-0 flex-grow-0 overflow-hidden', {
        'w-1/3': isTourMap,
        'w-1/2': !isTourMap,
        'w-full pt-0 border border-gray-300 shadow rounded-md': isTripSearch,
        'px-2 py-5': !isTripSearch,
      })}
    >
      {/* Measured rather than the padded outer box, so the static image is framed on
          exactly the area the dynamic map will occupy. */}
      <div ref={containerRef} className="h-full w-full">
        {renderMapSurface()}
      </div>
      <div style={{ display: 'none' }}>
        {!isTourMap && (
          <MapMarkerLegend
            ref={mapMarkerLegendRef}
            isPreferred={!doHideAgentPreferedHotelLabel}
            isPartnerRateAvailable={isAnyRateCodeSelected}
            isNoPartnerRateAvailable={isAnyRateCodeSelected}
          />
        )}
      </div>
    </div>
  );
};

DisplayMap.propTypes = {
  onMarkerClick: PropTypes.func.isRequired,
  hoveredMarkerKey: PropTypes.string,
  selectedMarkerKey: PropTypes.string,
  markers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tripId: PropTypes.string,
};

DisplayMap.defaultProps = {
  hoveredMarkerKey: undefined,
  selectedMarkerKey: undefined,
  tripId: undefined,
};
export default memo(DisplayMap);
